/**
 * Where an organization's or a partner's task output is delivered.
 *
 * One component on both records, because it is one object: the partner
 * card and the organization card differ only in whose endpoint they edit.
 *
 * Three states, and the distinction between the last two is the point.
 * An unsigned endpoint is a deliberate choice carrying a consequence —
 * the URL is the only credential — not a secret field somebody forgot to
 * fill in, so it is rendered as its own state rather than as a blank.
 *
 * The no-endpoint state names where deliveries go meanwhile. For an
 * organization under a partner that has one, they go to the partner, and
 * a card that said only "not configured" would be wrong about where the
 * data is going.
 *
 * The Events block chooses which of the two event kinds the receiver
 * gets. Both off is never sent: the API refuses it, and the card says so
 * in the API's words with the real action, disabling, beside it.
 */
import { ApiError } from '@optum/api-client/client';
import {
  deleteWebhookEndpoint,
  dropWebhookSigning,
  getWebhookEndpoint,
  rotateWebhookSecret,
  setWebhookEndpoint,
  testWebhookEndpoint,
} from '@optum/api-client/endpoints/webhook-endpoints';
import type {
  WebhookEndpoint,
  WebhookEndpointWithSecret,
  WebhookOwner,
} from '@optum/api-client/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { Button } from '../../ui/Button.js';
import { useToast } from '../../ui/useToast.js';
import { absoluteDateTime } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { useStepUp } from '../../lib/step-up.js';
import { Checkbox } from '../../ui/Checkbox.js';
import { useConfirm } from '../../ui/Confirm.js';
import { Badge, Input } from '../../ui/finixt.js';
import { WebhookSecretDialog } from './WebhookSecretDialog.js';

function hasSecret(
  value: WebhookEndpoint | WebhookEndpointWithSecret,
): value is WebhookEndpointWithSecret {
  return 'secret' in value && typeof value.secret === 'string';
}

function is404(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

export function WebhookCard({
  owner,
  fallsThroughTo,
}: {
  owner: WebhookOwner;
  /** Partner name, when this organization's deliveries would go there. */
  fallsThroughTo?: string | null;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [secret, setSecret] = useState<string | null>(null);
  const [url, setUrl] = useState('');
  const [signed, setSigned] = useState(true);
  // Issuing or withdrawing the signing secret demands a recent sign-in;
  // the step-up wrapper asks the operator to sign in again when refused.
  const [guardedRotate, rotateDialog] = useStepUp(rotateWebhookSecret);
  const [guardedDrop, dropDialog] = useStepUp(dropWebhookSigning);
  // Creating a signed endpoint returns its secret, so the agent asks for a fresh sign-in.
  const [guardedSave, saveDialog] = useStepUp(setWebhookEndpoint);

  const query = useQuery({
    queryKey: qk.webhookEndpoint(owner.kind, owner.id),
    queryFn: () => getWebhookEndpoint(owner),
    retry: (n, e) => (is404(e) ? false : n < 2),
  });

  const endpoint = query.data ?? null;

  useEffect(() => {
    if (endpoint) setUrl(endpoint.url);
  }, [endpoint]);

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: qk.webhookEndpoint(owner.kind, owner.id) });

  const fail = (verb: string) => (e: unknown) =>
    toast.error(`${verb} failed`, e instanceof ApiError ? e.message : String(e));

  const save = useMutation({
    mutationFn: () =>
      guardedSave(owner, {
        url,
        enabled: endpoint?.enabled ?? true,
        ...(endpoint ? {} : { signed }),
      }),
    onSuccess: (saved) => {
      toast.success('Webhook saved');
      if (hasSecret(saved)) setSecret(saved.secret);
      void invalidate();
    },
    onError: fail('Save'),
  });

  const toggleEnabled = useMutation({
    mutationFn: () =>
      setWebhookEndpoint(owner, {
        url: endpoint?.url ?? url,
        enabled: !(endpoint?.enabled ?? true),
        send_status: endpoint?.send_status ?? true,
        send_output: endpoint?.send_output ?? true,
      }),
    onSuccess: () => void invalidate(),
    onError: fail('Update'),
  });

  // The PUT replaces the record, so every flag travels every time.
  const setEvents = useMutation({
    mutationFn: (events: { send_status: boolean; send_output: boolean }) =>
      setWebhookEndpoint(owner, {
        url: endpoint?.url ?? url,
        enabled: endpoint?.enabled ?? true,
        ...events,
      }),
    onSuccess: () => void invalidate(),
    onError: fail('Update'),
  });

  const rotate = useMutation({
    mutationFn: () => guardedRotate(owner),
    onSuccess: (rotated) => {
      setSecret(rotated.secret);
      void invalidate();
    },
    onError: fail('Rotate'),
  });

  const dropSigning = useMutation({
    mutationFn: () => guardedDrop(owner),
    onSuccess: () => {
      toast.success('Signing dropped');
      void invalidate();
    },
    onError: fail('Drop signing'),
  });

  const remove = useMutation({
    mutationFn: () => deleteWebhookEndpoint(owner),
    onSuccess: () => {
      toast.success('Webhook removed');
      void invalidate();
    },
    onError: fail('Remove'),
  });

  const ping = useMutation({
    mutationFn: () => testWebhookEndpoint(owner),
    onSuccess: (result) => {
      if (result.status_code && result.status_code >= 200 && result.status_code < 300) {
        toast.success(`Receiver answered ${result.status_code} in ${result.elapsed_ms} ms`);
      } else {
        toast.error(
          'Test delivery was not accepted',
          result.error ?? `Receiver answered ${result.status_code}`,
        );
      }
    },
    onError: fail('Test'),
  });

  if (query.isPending && !is404(query.error)) return <LoadingBlock />;
  if (query.isError && !is404(query.error)) {
    return (
      <ErrorBlock message={query.error instanceof Error ? query.error.message : 'Failed to load'} />
    );
  }

  return (
    <div
      style={{
        borderRadius: 'var(--radius-card)',
        border: '1px solid var(--border-hairline)',
        background: 'var(--surface-card)',
        padding: 16,
      }}
    >
      {rotateDialog}
      {dropDialog}
      {saveDialog}
      <WebhookSecretDialog secret={secret} onClose={() => setSecret(null)} />
      {endpoint === null ? (
        <NoEndpoint
          url={url}
          signed={signed}
          onUrl={setUrl}
          onSigned={setSigned}
          onSave={() => save.mutate()}
          saving={save.isPending}
          fallsThroughTo={fallsThroughTo ?? null}
        />
      ) : (
        <Configured
          endpoint={endpoint}
          url={url}
          onUrl={setUrl}
          onSave={() => save.mutate()}
          saving={save.isPending}
          onToggleEnabled={() => toggleEnabled.mutate()}
          onEvents={(events) => setEvents.mutate(events)}
          onRotate={() => {
            void (async () => {
              const signedNow = endpoint.signed;
              if (
                await confirm({
                  title: signedNow ? 'Rotate signing secret?' : 'Adopt signing?',
                  description: signedNow
                    ? 'A new secret is issued and shown once. The current one keeps working until the overlap closes.'
                    : 'A secret is issued and shown once. Deliveries are signed from the next attempt.',
                  confirmLabel: signedNow ? 'Rotate' : 'Adopt signing',
                })
              ) {
                rotate.mutate();
              }
            })();
          }}
          onDropSigning={() => {
            void (async () => {
              if (
                await confirm({
                  title: 'Drop signing?',
                  description:
                    'Deliveries will carry no signature header. The receiver will no longer be able to prove a delivery came from Optum.',
                  confirmLabel: 'Drop signing',
                  tone: 'danger',
                })
              ) {
                dropSigning.mutate();
              }
            })();
          }}
          onRemove={() => {
            void (async () => {
              if (
                await confirm({
                  title: 'Remove endpoint?',
                  description: 'Task events will stop being delivered for this record.',
                  confirmLabel: 'Remove',
                  tone: 'danger',
                })
              ) {
                remove.mutate();
              }
            })();
          }}
          onPing={() => ping.mutate()}
          pinging={ping.isPending}
        />
      )}
    </div>
  );
}

function NoEndpoint({
  url,
  signed,
  onUrl,
  onSigned,
  onSave,
  saving,
  fallsThroughTo,
}: {
  url: string;
  signed: boolean;
  onUrl: (value: string) => void;
  onSigned: (value: boolean) => void;
  onSave: () => void;
  saving: boolean;
  fallsThroughTo: string | null;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
      <p style={{ fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>
        Task events are not delivered anywhere for this record.
      </p>
      {fallsThroughTo && (
        <p
          style={{
            borderRadius: 'var(--radius-control)',
            background: 'var(--surface-fill)',
            paddingLeft: 11,
            paddingRight: 11,
            paddingTop: 7,
            paddingBottom: 7,
            fontSize: 'var(--text-body-md)',
          }}
        >
          Its federated partner has an endpoint, so deliveries currently go to{' '}
          <span style={{ fontWeight: 'var(--weight-semibold)' }}>{fallsThroughTo}</span>.
        </p>
      )}
      <Input
        value={url}
        onChange={(e) => onUrl(e.target.value)}
        placeholder="https://your-receiver.example/hook"
        aria-label="Webhook URL"
      />
      <Checkbox
        checked={signed}
        onChange={(checked) => onSigned(checked)}
        label="Sign deliveries (recommended)"
      />
      {!signed && (
        <p
          style={{
            borderRadius: 'var(--radius-control)',
            border: '1px solid var(--warn)',
            borderStyle: 'dashed',
            background: 'var(--warn-tint)',
            paddingLeft: 11,
            paddingRight: 11,
            paddingTop: 7,
            paddingBottom: 7,
            fontSize: 'var(--text-body-md)',
          }}
        >
          Without a signature the URL is the only thing separating a genuine delivery from anyone
          who learns it. Use a long, unguessable path.
        </p>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="primary" onClick={onSave} disabled={saving || !url}>
          {saving ? 'Saving…' : 'Add endpoint'}
        </Button>
      </div>
    </div>
  );
}

function Configured({
  endpoint,
  url,
  onUrl,
  onSave,
  saving,
  onToggleEnabled,
  onEvents,
  onRotate,
  onDropSigning,
  onRemove,
  onPing,
  pinging,
}: {
  endpoint: WebhookEndpoint;
  url: string;
  onUrl: (value: string) => void;
  onSave: () => void;
  saving: boolean;
  onToggleEnabled: () => void;
  onEvents: (events: { send_status: boolean; send_output: boolean }) => void;
  onRotate: () => void;
  onDropSigning: () => void;
  onRemove: () => void;
  onPing: () => void;
  pinging: boolean;
}) {
  const overlapOpen =
    endpoint.previous_retires_at !== null && new Date(endpoint.previous_retires_at) > new Date();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <Badge tone={endpoint.enabled ? 'ok' : 'neutral'}>
          {endpoint.enabled ? 'enabled' : 'disabled'}
        </Badge>
        <Badge tone={endpoint.signed ? 'ok' : 'warn'}>
          {endpoint.signed ? 'signed' : 'unsigned'}
        </Badge>
      </div>

      <div style={{ display: 'flex', gap: 7 }}>
        <Input value={url} onChange={(e) => onUrl(e.target.value)} aria-label="Webhook URL" />
        <Button variant="secondary" onClick={onSave} disabled={saving || url === endpoint.url}>
          {saving ? 'Saving…' : 'Save URL'}
        </Button>
      </div>

      <EventsBlock
        sendStatus={endpoint.send_status}
        sendOutput={endpoint.send_output}
        onChange={onEvents}
        onDisable={onToggleEnabled}
        enabled={endpoint.enabled}
      />

      {endpoint.signed ? (
        <p style={{ fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>
          Deliveries carry{' '}
          <code style={{ fontSize: 'var(--text-meta-lg)' }} className="mono">
            X-Optum-Signature
          </code>
          .
          {overlapOpen && (
            <span style={{ color: 'var(--warn)' }}>
              {' '}
              The previous secret retires {absoluteDateTime(
                endpoint.previous_retires_at as string,
              )}{' '}
              — both signatures are sent until then.
            </span>
          )}
        </p>
      ) : (
        <p
          style={{
            borderRadius: 'var(--radius-control)',
            border: '1px solid var(--warn)',
            borderStyle: 'dashed',
            background: 'var(--warn-tint)',
            paddingLeft: 11,
            paddingRight: 11,
            paddingTop: 7,
            paddingBottom: 7,
            fontSize: 'var(--text-body-md)',
          }}
        >
          No signature header is sent. The URL is the only thing separating a genuine delivery from
          anyone who learns it.
        </p>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
        <Button variant="secondary" onClick={onPing} disabled={pinging}>
          {pinging ? 'Sending…' : 'Send test'}
        </Button>
        <Button variant="secondary" onClick={onToggleEnabled}>
          {endpoint.enabled ? 'Disable' : 'Enable'}
        </Button>
        <Button variant="secondary" onClick={onRotate}>
          {endpoint.signed ? 'Rotate secret' : 'Adopt signing'}
        </Button>
        {endpoint.signed && (
          <Button variant="secondary" onClick={onDropSigning}>
            Drop signing
          </Button>
        )}
        <Button variant="danger" onClick={onRemove}>
          Remove
        </Button>
      </div>
    </div>
  );
}

/**
 * Which event kinds the receiver gets. Unticking the last one is shown as
 * the refusal the API would give, with disabling offered instead, rather
 * than sent and bounced.
 */
function EventsBlock({
  sendStatus,
  sendOutput,
  enabled,
  onChange,
  onDisable,
}: {
  sendStatus: boolean;
  sendOutput: boolean;
  enabled: boolean;
  onChange: (events: { send_status: boolean; send_output: boolean }) => void;
  onDisable: () => void;
}) {
  const [refused, setRefused] = useState(false);

  const change = (next: { send_status: boolean; send_output: boolean }) => {
    // Both off is not a configuration; the card says so and sends nothing.
    if (!next.send_status && !next.send_output) {
      setRefused(true);
      return;
    }
    setRefused(false);
    onChange(next);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 7,
        borderRadius: 'var(--radius-input)',
        border: '1px solid var(--border-hairline)',
        padding: 11,
      }}
    >
      <span
        style={{
          fontSize: 'var(--text-meta-lg)',
          fontWeight: 'var(--weight-semibold)',
          color: 'var(--text-muted)',
        }}
      >
        Events
      </span>
      <label
        style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 'var(--text-body-md)' }}
      >
        <Checkbox
          checked={sendStatus}
          aria-label="task.status"
          onChange={(checked) => change({ send_status: checked, send_output: sendOutput })}
        />
        <code style={{ fontSize: 'var(--text-meta-lg)' }} className="mono">
          task.status
        </code>
        <span style={{ color: 'var(--text-muted)' }}>
          every status write: pending, running, completed, failed
        </span>
      </label>
      <label
        style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 'var(--text-body-md)' }}
      >
        <Checkbox
          checked={sendOutput}
          aria-label="task.output"
          onChange={(checked) => change({ send_status: sendStatus, send_output: checked })}
        />
        <code style={{ fontSize: 'var(--text-meta-lg)' }} className="mono">
          task.output
        </code>
        <span style={{ color: 'var(--text-muted)' }}>
          once, when the task ends, whatever it ended as
        </span>
      </label>
      {refused && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 7,
            borderRadius: 'var(--radius-input)',
            border: '1px solid var(--danger-line)',
            borderStyle: 'dashed',
            background: 'var(--danger-tint)',
            paddingLeft: 11,
            paddingRight: 11,
            paddingTop: 7,
            paddingBottom: 7,
            fontSize: 'var(--text-body-md)',
          }}
        >
          <span style={{ color: 'var(--danger)' }}>
            At least one event must be on. To stop every delivery, disable the endpoint instead.
          </span>
          {enabled && (
            <Button size="sm" variant="secondary" onClick={onDisable}>
              Disable endpoint
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
